/**
 * Resolves a service base URL (API, transfer, PDF processing, SignalR) to an absolute URL.
 *
 * Behind a same-origin reverse proxy (nginx in the Docker image, Vercel middleware) the
 * `VITE_APP_*_BASE_URL` variables hold a path such as `/api`, so the browser never learns the
 * backend host. The browser resolves such paths on its own, but clients outside the page — the
 * desktop agent (OfficeGrant/RemoteGrant) and the Chrome extension — receive the value as a plain
 * string and need a full URL. This helper prefixes same-origin paths with the page origin and
 * returns every other value (absolute URLs, empty strings, unsubstituted placeholders) unchanged.
 *
 * @param value Raw base URL, e.g. `import.meta.env.VITE_APP_API_BASE_URL`.
 * @param origin Origin to resolve against; defaults to the current page origin.
 * @returns The trimmed value, prefixed with `origin` when it is a same-origin path.
 */
export function toAbsoluteServiceUrl(
  value: string | undefined,
  origin: string = window.location.origin,
): string {
  const trimmed = String(value ?? '').trim();
  // `//host/path` is protocol-relative, not a same-origin path.
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) {
    return trimmed;
  }
  return `${origin.replace(/\/+$/, '')}${trimmed}`;
}
